import { constant, isNil, multiply } from 'lodash/fp';
import { createSelector, ParametricSelector } from 'reselect';
import {
    DateService,
    TimeService,
    DateTimeService,
    WallClockDateService,
    Time,
    Date,
    DateTimeUtils,
    DateTime,
    AppClockService,
} from '@mews/framework';
import { ifElse } from '@mews/fp';
import { ApplicationState, WithScope } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { StructuredDate } from '@optimus-web/core';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    getServiceEnterpriseEditableHistoryIntervalMilliseconds,
    getServiceEnterpriseIanaTimeZoneIdentifier,
} from '@mews-commander/legacy-entities/src/selectors/services';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { getReleaseDate, getReleaseTime } from './options';
import { getStartDate, getEndDate, getStartTime, getEndTime } from './order';
import { getMaximumAvailableEditableHistoryIntervalMilliseconds } from './entities';

const orderDateTimeWindowHourExtension = 24;

export function createDateObjectSelector(getDateString: ParametricSelector<ApplicationState, WithScope, string>) {
    return createSelector(
        getDateString,
        getCurrentEnterpriseIanaTimeZoneIdentifier,
        ifElse(isNil, constant(null), (date: string, timezone: string) => DateService.parse(date, timezone))
    );
}

export function createTimeObjectSelector(getTimeString: ParametricSelector<ApplicationState, WithScope, string>) {
    return createSelector(
        getTimeString,
        ifElse(isNil, constant(null), (time: string) => TimeService.parse(time))
    );
}

export const getStartDateObject = createDateObjectSelector(getStartDate);
export const getEndDateObject = createDateObjectSelector(getEndDate);
export const getStartTimeObject = createTimeObjectSelector(getStartTime);
export const getEndTimeObject = createTimeObjectSelector(getEndTime);

export const getStartStructuredDate = createSelector(getStartDateObject, (startDate) =>
    startDate ? DateTimeUtils.dateToStructuredDate(startDate) : undefined
);
export const getEndStructuredDate = createSelector(getEndDateObject, (endDate) =>
    endDate ? DateTimeUtils.dateToStructuredDate(endDate) : undefined
);

export function createDateTimeSelector(getDateObject: $TSFixMe, getTimeObject: $TSFixMe) {
    return createSelector(getDateObject, getTimeObject, (date: Date, time: Time) => {
        if (!date) {
            return null;
        }
        return time ? DateTimeService.fromDateAndTime(date, time) : DateTimeService.fromDate(date);
    });
}

export const getStartDateTime = createDateTimeSelector(getStartDateObject, getStartTimeObject);
export const getEndDateTime = createDateTimeSelector(getEndDateObject, getEndTimeObject);

export const getIsNowWithinExtendedOrderDateTimeWindow = createSelector(
    getStartDateTime,
    getEndDateTime,
    getCurrentEnterpriseAppDateTimeNow,
    (startDateTime, endDateTime, nowDateTime) => {
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'DateTime'.
        const extendedStart = DateTimeService.subtractHours(startDateTime, orderDateTimeWindowHourExtension);
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'DateTime'.
        const extendedEnd = DateTimeService.addHours(endDateTime, orderDateTimeWindowHourExtension);
        return (
            DateTimeService.isGreater(nowDateTime, extendedStart) && DateTimeService.isLess(nowDateTime, extendedEnd)
        );
    }
);

export const getMinStructuredDate = createSelector<
    ApplicationState,
    { appClockService: AppClockService },
    number | undefined,
    DateTime,
    StructuredDate
>(
    getMaximumAvailableEditableHistoryIntervalMilliseconds,
    getCurrentEnterpriseAppDateTimeNow,
    (editableHistoryIntervalMilliseconds, now) => {
        const minDateTime = DateTimeService.addMilliseconds(
            now,
            -1 * (editableHistoryIntervalMilliseconds as $TSFixMe)
        );
        const minDate = DateTimeService.toDate(minDateTime);

        return minDate && DateTimeUtils.dateToStructuredDate(minDate);
    }
);

export const getServiceStartDateUtc: (
    state: ApplicationState,
    params: { serviceId: string } & WithScope
) => string | null = createServiceUtcDateSelector(
    getServiceEnterpriseIanaTimeZoneIdentifier,
    getStartDate,
    getStartTime,
    getTimeUnitByServiceId
);

export const getServiceEndDateUtc: (
    state: ApplicationState,
    params: { serviceId: string } & WithScope
) => string | null = createServiceUtcDateSelector(
    getServiceEnterpriseIanaTimeZoneIdentifier,
    getEndDate,
    getEndTime,
    getTimeUnitByServiceId
);

function createServiceUtcDateSelector(
    timezoneSelector: $TSFixMe,
    dateSelector: $TSFixMe,
    timeSelector: $TSFixMe,
    timeUnitSelector: $TSFixMe
) {
    return createSelector(
        timezoneSelector,
        dateSelector,
        timeSelector,
        timeUnitSelector,
        (timezone, dateString, timeString: string | null, timeUnit) => {
            if (!dateString) {
                return null;
            }
            // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'string'.
            const date = DateService.parse(dateString, timezone);
            const time = timeUnit === TimeUnit.Hour && timeString ? TimeService.parse(timeString) : null;
            const dateTime = time ? DateTimeService.fromDateAndTime(date, time) : DateTimeService.fromDate(date);
            const dateTimeUtc = DateTimeService.toUtc(dateTime);

            return dateTimeUtc.value;
        }
    );
}

export const hasSelectedStartDateOutOfEditableHistoryWindow = createSelector(
    getStartDateObject,
    getCurrentEnterpriseAppDateTimeNow,
    getServiceEnterpriseEditableHistoryIntervalMilliseconds,
    (startDate, now, editableHistoryIntervalMilliseconds) => {
        const serviceMinDateTime = DateTimeService.addMilliseconds(
            now,
            multiply(-1, editableHistoryIntervalMilliseconds)
        );

        const serviceMinDate = DateTimeService.toDate(serviceMinDateTime);

        // Service date is constructed in the enterprise timezone, while startDate is selected in current enterprise timezone
        // To fix this, range date picker should work with wallclock dates in the first place
        // @ts-expect-error $TSFixMeError: Object is possibly 'null'.
        const comparableStartDate = WallClockDateService.parse(startDate.value);
        const comparableMinDate = WallClockDateService.parse(serviceMinDate.value);

        return WallClockDateService.isLess(comparableStartDate, comparableMinDate);
    }
);

export const getSelectedServiceReleaseDateTime = createSelector(
    getCurrentDateTimeLocalizationInfo,
    getServiceEnterpriseIanaTimeZoneIdentifier,
    getReleaseDate,
    getReleaseTime,
    (localization, timezone, releaseDate, releaseTime) => {
        if (!(releaseDate && releaseTime)) {
            return null;
        }

        const date = DateService.fromString(releaseDate, localization, timezone);
        const time = TimeService.fromShortString(releaseTime, localization);
        const dateTime = DateTimeService.fromDateAndTime(date, time);

        return dateTime;
    }
);

export const getSelectedServiceReleasedUtc = createSelector(getSelectedServiceReleaseDateTime, (releaseDateTime) => {
    if (!releaseDateTime) {
        return null;
    }

    const { value: dateTimeUtc } = DateTimeService.toUtc(releaseDateTime);

    return dateTimeUtc;
});

export const getDateTimeUtcString = (dateString: $TSFixMe, timeString: $TSFixMe, ianaTimeZoneIdentifier: $TSFixMe) => {
    const date = DateService.parse(dateString, ianaTimeZoneIdentifier);
    const time = TimeService.parse(timeString);
    const dateTime = DateTimeService.fromDateAndTime(date, time);
    const dateTimeUtc = DateTimeService.toUtc(dateTime);
    const { value: dateTimeUtcString } = dateTimeUtc;

    return dateTimeUtcString;
};
